import {
  Button,
  ButtonProps,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  TextField as MuiTextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import kebabCase from "lodash/kebabCase";
import { useLayoutEffect, useRef, useState } from "react";

import {
  FieldsData,
  GroupFieldType,
  GroupSubField,
  StatusQueryState,
} from "../../types";
import BaseCard, { BaseCardProps } from "../BaseCard/base-card";
import { ErrorCard } from "../ErrorCard/error-card";
import Fields from "../Fields/fields";
import { LoadingCard, SkeletonProps } from "../LoadingCard/loading-card";

import { processFieldValue, processGroupField } from "./fields-card.helper";

function buildValuesMap(
  fields: { inputName?: string; inputValue?: unknown; value?: unknown }[],
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.inputName) {
      values[f.inputName] = f.inputValue ?? f.value;
    }
  }
  return values;
}

type Props = Pick<BaseCardProps, "title" | "subtitle"> & {
  gridItemProps?: FieldsData["gridItemProps"];
  gridContainerProps?: FieldsData["gridItemProps"];
  skeleton: SkeletonProps;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: (body: FieldsCardBody | null) => void;
} & (
    | StatusQueryState<FieldsData[]>
    | EditStatus<FieldsData[]>
    | { status: "saving"; data: FieldsData[] }
  );

type EditData = {
  isEdited: boolean;
  isError: boolean;
  fields: ExtendedFieldsData[];
};

type ValuesMap = Record<string, unknown>;
type SerializedFieldValue = string | number | readonly string[] | null;
type SerializedGroupValue = Record<string, SerializedFieldValue>;
export type FieldsCardBodyValue = SerializedFieldValue | SerializedGroupValue;
export type FieldsCardBody = Record<string, FieldsCardBodyValue>;
type EditableField = Extract<ExtendedFieldsData, { editable: true }>;
type TextOrNumberEditableField = Extract<
  EditableField,
  { inputType: "text" | "number" }
>;
type SelectEditableField = Extract<EditableField, { inputType: "select" }>;
type DateEditableField = Extract<EditableField, { inputType: "date" }>;
type ExtendedGroupSubField = GroupSubField & {
  error?: boolean;
  helperText?: string;
};

const INIT_EDIT_DATA: EditData = {
  isEdited: false,
  isError: false,
  fields: [] as ExtendedFieldsData[],
};

function FieldsCard(props: Props): React.JSX.Element {
  const [editData, setEditData] = useState(INIT_EDIT_DATA);

  if (props.status === "pending") {
    return (
      <LoadingCard
        data-testid="ds-fields-card-loading"
        title={props.title}
        gridContainerProps={props.gridContainerProps}
        gridItemProps={props.gridItemProps}
        skeleton={props.skeleton}
      />
    );
  }

  if (props.status === "error") {
    return (
      <ErrorCard
        data-testid="ds-fields-card-error"
        title={props.title}
        error={props.error}
      />
    );
  }

  function handleSave(): void {
    if (props.onSave) {
      let changes = false;
      const body = editData.fields.reduce<FieldsCardBody>((acc, field, i) => {
        if (!field.editable) {
          return acc;
        }

        if (
          field.value?.toString() !== props.data?.[i]?.value?.toString() ||
          field.inputValue?.toString() !==
            props.data?.[i]?.inputValue?.toString()
        ) {
          changes = true;
        }

        if (field.inputType === "group") {
          // Handle grouped fields - collect values from sub-fields
          if (field.inputProps?.fields) {
            const originalGroupValue =
              (props.data?.[i]?.value as SerializedGroupValue | null) ?? null;

            const { groupValue, hasChanges } = processGroupField(
              field as GroupFieldType,
              originalGroupValue,
            );

            if (hasChanges) {
              changes = true;
            }
            acc[field.inputName] = groupValue;
          }
        } else {
          acc[field.inputName] = processFieldValue(field);
        }
        return acc;
      }, {});

      if (changes) {
        props.onSave(body);
      } else {
        props.onSave(null);
      }
    }
  }

  return (
    <BaseCard
      data-testid="ds-fields-card"
      title={props.title}
      subtitle={props.subtitle}
      action={
        <EditActionCard
          status={editData.isError ? "error" : props.status}
          isEdited={editData.isEdited}
          editButton={{ onClick: props.onEdit }}
          cancelButton={{ onClick: props.onCancel }}
          saveButton={{ onClick: props.onSave ? handleSave : undefined }}
        />
      }
    >
      {props.status === "edit" || props.status === "saving" ? (
        <FieldsEditing
          data={editData.fields}
          originalData={props.data}
          setData={setEditData}
          gridContainerProps={props.gridContainerProps}
          gridItemProps={props.gridItemProps}
        />
      ) : (
        <Fields
          data-testid="ds-fields-card-view"
          data={props.data.filter((f) => f.visibleViewMode !== false)}
          gridContainerProps={props.gridContainerProps}
          gridItemProps={props.gridItemProps}
        />
      )}
    </BaseCard>
  );
}

function FieldsEditing({
  data,
  originalData,
  setData,
  gridContainerProps,
  gridItemProps,
}: Pick<Props, "gridContainerProps" | "gridItemProps"> & {
  data: ExtendedFieldsData[];
  originalData: FieldsData[];
  setData: React.Dispatch<React.SetStateAction<EditData>>;
}): React.JSX.Element {
  useLayoutEffect(() => {
    setData({
      isEdited: false,
      isError: false,
      fields: cloneDeep(
        originalData.filter((f) => f.visibleEditMode !== false),
      ),
    });

    return () => {
      setData(INIT_EDIT_DATA);
    };
  }, [originalData, setData]);

  const validateFields = useRef(
    debounce((index: number, newEditedData: ExtendedFieldsData[]) => {
      const allValues = buildValuesMap(newEditedData);

      const fields = newEditedData.map(validateField);
      const isError = fields.some((f) => {
        // Skip fields hidden by visibleWhen
        if (f.visibleWhen && !f.visibleWhen(allValues)) {
          return false;
        }
        // Check if field itself has error
        if (f.error) return true;
        // Check if any sub-field in a group has error
        if (f.inputType === "group" && f.inputProps?.fields) {
          return f.inputProps.fields.some(
            (sf) => (sf as ExtendedFieldsData).error,
          );
        }
        return false;
      });
      setData({ isEdited: true, isError, fields });

      function validateField(
        editedData: ExtendedFieldsData,
        i: number,
      ): ExtendedFieldsData {
        if (i !== index) {
          return editedData;
        }

        // Skip validation for fields hidden by visibleWhen
        if (editedData.visibleWhen && !editedData.visibleWhen(allValues)) {
          return { ...editedData, error: false, helperText: undefined };
        }

        // Validate grouped fields' sub-fields
        if (editedData.inputType === "group" && editedData.inputProps?.fields) {
          const groupValues = buildValuesMap(editedData.inputProps.fields);

          const validatedSubFields = editedData.inputProps.fields.map(
            (subField: GroupSubField) => {
              const extendedSubField = subField as ExtendedGroupSubField;

              if (
                extendedSubField.visibleWhen &&
                !extendedSubField.visibleWhen(groupValues)
              ) {
                return {
                  ...extendedSubField,
                  error: false,
                  helperText: undefined,
                };
              }

              const subValidators = extendedSubField.validators || [];
              for (const validator of subValidators) {
                const errorMgs = validator(
                  extendedSubField as FieldsData,
                  groupValues,
                );
                if (errorMgs) {
                  return {
                    ...extendedSubField,
                    error: true,
                    helperText: errorMgs,
                  };
                }
              }
              return {
                ...extendedSubField,
                error: false,
                helperText: undefined,
              };
            },
          );
          return {
            ...editedData,
            inputProps: {
              ...editedData.inputProps,
              fields: validatedSubFields as GroupSubField[],
            },
          };
        }

        // Validate regular fields
        const validators = editedData.validators || [];
        for (const validator of validators) {
          const errorMgs = validator(editedData);
          if (errorMgs) {
            return { ...editedData, error: true, helperText: errorMgs };
          }
        }
        return { ...editedData, error: false, helperText: undefined };
      }
    }, 500),
  );

  function handleChange(index: number, newField: ExtendedFieldsData): void {
    const editedData = data.map((d, i) => (i === index ? newField : d));
    setData((prev) => ({ ...prev, fields: editedData }));
    validateFields.current(index, editedData);
  }

  const allValues = buildValuesMap(data);

  const renderValueOrInput = renderEditable(handleChange, allValues);

  return (
    <Fields
      data-testid="ds-fields-card-editing"
      data={data
        .map(renderValueOrInput)
        .filter((f): f is NonNullable<typeof f> => f !== null)}
      gridContainerProps={gridContainerProps}
      gridItemProps={gridItemProps}
    />
  );
}

function InputByType({
  field,
  onChange,
}: {
  field: EditableField;
  onChange: (newField: ExtendedFieldsData) => void;
}): React.JSX.Element {
  const strLabel = field.label
    ? typeof field.label === "string"
      ? field.label
      : field.label.name
    : "";
  const testId = `ds-fields-card-input-${strLabel || "unlabeled"}`;

  switch (field.inputType) {
    case "number":
    case "text": {
      return renderTextInput({ field, onChange, strLabel, testId });
    }
    case "select": {
      return renderSelectInput({ field, onChange, strLabel, testId });
    }
    case "date":
      return renderDateInput({ field, onChange, strLabel, testId });
    default:
      throw new Error("Invalid input type");
  }
}

function renderTextInput({
  field,
  onChange,
  strLabel,
  testId,
}: {
  field: TextOrNumberEditableField;
  onChange: (newField: ExtendedFieldsData) => void;
  strLabel: string;
  testId: string;
}): React.JSX.Element {
  const { error, helperText, inputProps, inputType, value } = field;
  const textInputProps = inputProps;

  return (
    <MuiTextField
      data-testid={testId}
      fullWidth
      size="small"
      value={value ?? ""}
      error={error}
      label={strLabel}
      helperText={helperText}
      onChange={(e) => onChange({ ...field, value: e.target.value })}
      type={inputType === "number" ? "number" : undefined}
      {...textInputProps}
      slotProps={{
        ...textInputProps?.slotProps,
        htmlInput: {
          ...textInputProps?.slotProps?.htmlInput,
          inputMode: inputType === "number" ? "numeric" : undefined,
        },
      }}
    />
  );
}

function renderSelectInput({
  field,
  onChange,
  strLabel,
  testId,
}: {
  field: SelectEditableField;
  onChange: (newField: ExtendedFieldsData) => void;
  strLabel: string;
  testId: string;
}): React.JSX.Element {
  const { error, helperText, inputProps, inputValue, value } = field;
  const { options, helperText: selectHelperText, ...rest } = inputProps || {};

  return (
    <FormControl fullWidth size="small">
      <InputLabel id={`fields-card-${strLabel}-label`} sx={{ opacity: 0.54 }}>
        {strLabel}
      </InputLabel>
      <MuiSelect
        data-testid={testId}
        label={strLabel}
        labelId={`fields-card-${strLabel}-label`}
        value={inputValue ?? value ?? ""}
        error={error}
        onChange={(e) => onChange({ ...field, inputValue: e.target.value })}
        {...rest}
      >
        {options?.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ alignItems: "flex-start" }}
          >
            {option.displayLabel ?? option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {(helperText ?? selectHelperText) ? (
        <FormHelperText>{helperText ?? selectHelperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

function renderDateInput({
  field,
  onChange,
  strLabel,
  testId,
}: {
  field: DateEditableField;
  onChange: (newField: ExtendedFieldsData) => void;
  strLabel: string;
  testId: string;
}): React.JSX.Element {
  const { error, helperText, inputProps, value } = field;

  return (
    <DatePicker
      data-testid={testId}
      label={strLabel}
      value={value}
      sx={{
        maxWidth: "100%",
        width: "100%",
      }}
      onChange={(newValue) => onChange({ ...field, value: newValue })}
      {...inputProps}
      slotProps={{
        ...inputProps?.slotProps,
        textField: {
          size: "small",
          error,
          helperText,
          fullWidth: true,
          ...inputProps?.slotProps?.textField,
        },
      }}
    />
  );
}

function renderEditable(
  onChange: (index: number, newField: ExtendedFieldsData) => void,
  allValues: ValuesMap,
): (d: FieldsData, i: number) => FieldsData | null {
  return (d: FieldsData, i: number): FieldsData | null => {
    // Check top-level visibleWhen
    if (d.visibleWhen && !d.visibleWhen(allValues)) {
      return null;
    }

    // Handle grouped fields in edit mode
    if (d.editable && d.inputType === "group" && d.inputProps?.fields) {
      const subFields = d.inputProps.fields as ExtendedGroupSubField[];

      // Collect current values from all sub-fields for visibleWhen checks
      const groupValues = buildValuesMap(subFields);

      return {
        ...d,
        element: (
          <Grid
            container
            spacing={d.inputProps.grid?.spacing || 0}
            columns={d.inputProps.grid?.columns || 12}
          >
            {subFields.map((subField, idx) => {
              const subKey = subField.label
                ? typeof subField.label === "string"
                  ? subField.label
                  : subField.label.name
                : `sub-field-${idx}`;

              // Only render editable sub-fields as inputs
              if (!subField.editable) {
                return null;
              }

              // Check visibleWhen condition
              if (subField.visibleWhen && !subField.visibleWhen(groupValues)) {
                return null;
              }

              // Ensure value is a string for text/number inputs
              const normalizedSubField =
                subField.inputType === "text" || subField.inputType === "number"
                  ? ({
                      ...subField,
                      value: subField.value ?? "",
                    } as EditableField)
                  : (subField as EditableField);

              return (
                <Grid
                  key={subKey}
                  data-testid={`ds-field-group-edit-item-${kebabCase(subKey)}`}
                  {...subField.gridItemProps}
                >
                  <InputByType
                    field={normalizedSubField}
                    onChange={(newSubField) => {
                      // Update sub-field in parent group
                      const newFields = subFields.map((f, j) =>
                        j === idx ? newSubField : f,
                      );
                      const newGroupField = {
                        ...d,
                        inputProps: { ...d.inputProps, fields: newFields },
                      } as ExtendedFieldsData;
                      onChange(i, newGroupField);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        ),
      };
    }

    // Handle regular editable fields
    return d.editable
      ? {
          ...d,
          element: (
            <InputByType
              field={d}
              onChange={(newField) => onChange(i, newField)}
            />
          ),
        }
      : d;
  };
}

function EditActionCard({
  status,
  isEdited = true,
  editButton,
  cancelButton,
  saveButton,
}: {
  status: "edit" | "saving" | "success" | "error";
  isEdited?: boolean;
  editButton?: ButtonProps;
  cancelButton?: ButtonProps;
  saveButton?: ButtonProps;
}): React.JSX.Element {
  if (status === "success" && editButton?.onClick) {
    return (
      <Button
        data-testid="ds-fields-card-edit-button"
        variant="text"
        {...editButton}
      >
        {editButton.children ?? "EDIT"}
      </Button>
    );
  }

  return (
    <>
      {cancelButton?.onClick ? (
        <Button
          data-testid="ds-fields-card-cancel-button"
          variant="text"
          disabled={status === "saving"}
          {...cancelButton}
        >
          {cancelButton.children ?? "CANCEL"}
        </Button>
      ) : null}
      {saveButton?.onClick ? (
        <Button
          data-testid="ds-fields-card-save-button"
          variant="contained"
          loading={status === "saving"}
          disabled={!isEdited || status === "error"}
          {...saveButton}
        >
          {saveButton.children ?? "SAVE"}
        </Button>
      ) : null}
    </>
  );
}

type ExtendedFieldsData = FieldsData & {
  error?: boolean;
  helperText?: string;
};

type EditStatus<T> = {
  status: "edit";
  data: T;
  error?: Error | null;
};

export { FieldsCard };
